import { useEffect, useRef, useState } from 'react';

const STATUS_ENDPOINT = 'https://video.radio-suton.de/api/status';
const POLL_INTERVAL_MS = 30_000;
const FAST_POLL_MS = 5_000;

export interface LiveVideoStatus {
  live: boolean;
  source?: 'studio' | 'mobile';
  started_at?: string;
  viewers?: number;
  stream_url?: string;
}

export interface UseLiveVideoStatusOptions {
  /** true → poll svakih 5s (npr. dok je korisnik na LiveVideoScreen) */
  fastPoll?: boolean;
  /** false → ne polluj uopće (uštedi mrežne pozive) */
  enabled?: boolean;
}

export interface UseLiveVideoStatusResult {
  status: LiveVideoStatus;
  error: string | null;
}

const DEFAULT_STATUS: LiveVideoStatus = { live: false };

/**
 * Polluje MediaMTX status endpoint i izlaže live state cijeloj aplikaciji.
 *
 * — Na error-u zadržava prethodno (last-known) status, samo izlaže `error`.
 * — `AbortController` otkazuje in-flight request prije sljedećeg ticka i na unmount.
 * — Brz polling (5s) automatski usporava na 30s kad se `fastPoll` flag isključi.
 */
export function useLiveVideoStatus(
  options: UseLiveVideoStatusOptions = {}
): UseLiveVideoStatusResult {
  const { fastPoll = false, enabled = true } = options;
  const [status, setStatus] = useState<LiveVideoStatus>(DEFAULT_STATUS);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const interval = fastPoll ? FAST_POLL_MS : POLL_INTERVAL_MS;

    const fetchStatus = async (): Promise<void> => {
      if (cancelled) return;
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const res = await fetch(STATUS_ENDPOINT, {
          signal: ctrl.signal,
          cache: 'no-store',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as LiveVideoStatus;
        if (cancelled) return;
        setStatus({
          live: Boolean(data?.live),
          source: data?.source,
          started_at: data?.started_at,
          viewers: typeof data?.viewers === 'number' ? data.viewers : undefined,
          stream_url: data?.stream_url,
        });
        setError(null);
      } catch (e: unknown) {
        if ((e as { name?: string })?.name === 'AbortError') return;
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : 'Network error';
        setError(msg);
      }
    };

    void fetchStatus();
    const id = window.setInterval(() => void fetchStatus(), interval);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [fastPoll, enabled]);

  return { status, error };
}
